// src/components/file/SelectionBar.jsx
import React, { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faLink, faPenToSquare, faFolderTree, faTag, faTrash } from '@fortawesome/free-solid-svg-icons';

import ActionMenu from '../common/ActionMenu';
import BulkTagModal from '../BulkTagModal';
import BulkMetadataModal from '../BulkMetadataModal';
import BulkDownloadModal from '../BulkDownloadModal';
import BulkProjectModal from '../BulkProjectModal';
import BulkDerivationModal from '../BulkDerivationModal';
import { WorkspaceContext } from '../../context/WorkspaceContext';
import '../../css/SelectionBar.css';

const EDIT_ROLES = ['editor', 'admin', 'owner'];

/**
 * What you can do with the files you have selected.
 *
 * These actions used to be injected into the command bar itself — a fixed-height
 * row with no wrap. Selecting a file pushed eight more controls into it, and
 * under the browser's `overflow: hidden` the ones that didn't fit became
 * unreachable rather than merely cramped. They live in their own bar now, so
 * the command bar's contents never change with the selection, and the rarer
 * actions sit in an overflow menu so this bar can't outgrow its row either.
 */
export default function SelectionBar({
    selectedFiles = [],
    resultCount = 0,
    allSelected = false,
    busy = false,
    onSelectAll,
    onClearSelection,
    onDelete,
    onFilesChanged,
}) {
    const [openModal, setOpenModal] = useState(null); // download | tags | metadata | projects | source
    const { activeWorkspaceRole } = useContext(WorkspaceContext);
    const canEdit = EDIT_ROLES.includes(activeWorkspaceRole);
    const isOwner = activeWorkspaceRole === 'owner';

    const count = selectedFiles.length;
    if (count === 0) return null;

    const close = () => setOpenModal(null);
    const applied = () => onFilesChanged?.();
    const noun = count === 1 ? 'file' : 'files';

    const overflowItems = [
        canEdit && {
            id: 'projects',
            label: 'Add to a project',
            icon: <FontAwesomeIcon icon={faFolderTree} />,
            onSelect: () => setOpenModal('projects'),
        },
        canEdit && {
            id: 'source',
            label: 'Link to a source file',
            description: 'Or set the pipeline stage',
            icon: <FontAwesomeIcon icon={faLink} />,
            onSelect: () => setOpenModal('source'),
        },
        isOwner && {
            id: 'delete',
            label: `Delete ${count.toLocaleString()} ${noun}`,
            icon: <FontAwesomeIcon icon={faTrash} />,
            danger: true,
            separatorBefore: true,
            onSelect: onDelete,
        },
    ].filter(Boolean);

    return (
        <>
            <div className="fb-selection-bar" role="group" aria-label="Selection actions" aria-busy={busy}>
                <p className="fb-selection-count">
                    <span aria-live="polite">
                        <strong>{count.toLocaleString()}</strong> selected
                    </span>
                </p>

                <div className="fb-selection-scope">
                    {!allSelected && resultCount > count && (
                        <button type="button" className="fb-selection-link" onClick={onSelectAll}>
                            Select all {resultCount.toLocaleString()}
                        </button>
                    )}
                    <button
                        type="button"
                        className="fb-selection-link"
                        onClick={onClearSelection}
                        title="Clear selection (Esc)"
                    >
                        Clear
                    </button>
                </div>

                <div className="fb-selection-actions">
                    <button
                        type="button"
                        className="qd-btn qd-btn--secondary qd-btn--sm"
                        onClick={() => setOpenModal('download')}
                        disabled={busy}
                    >
                        <FontAwesomeIcon icon={faDownload} /> Download
                    </button>

                    {canEdit && (
                        <>
                            <button
                                type="button"
                                className="qd-btn qd-btn--secondary qd-btn--sm"
                                onClick={() => setOpenModal('tags')}
                                disabled={busy}
                            >
                                <FontAwesomeIcon icon={faTag} /> Tags
                            </button>
                            <button
                                type="button"
                                className="qd-btn qd-btn--secondary qd-btn--sm"
                                onClick={() => setOpenModal('metadata')}
                                disabled={busy}
                            >
                                <FontAwesomeIcon icon={faPenToSquare} /> Metadata
                            </button>
                        </>
                    )}

                    {overflowItems.length > 0 && (
                        <ActionMenu
                            items={overflowItems}
                            label={`More actions for the selected ${noun}`}
                            triggerClassName="qd-btn qd-btn--secondary qd-btn--sm qd-btn--icon"
                        />
                    )}
                </div>
            </div>

            {openModal === 'download' && createPortal(
                <BulkDownloadModal files={selectedFiles} onClose={close} />, document.body,
            )}
            {openModal === 'tags' && createPortal(
                <BulkTagModal files={selectedFiles} onApplied={applied} onClose={close} />, document.body,
            )}
            {openModal === 'metadata' && createPortal(
                <BulkMetadataModal files={selectedFiles} onApplied={applied} onClose={close} />, document.body,
            )}
            {openModal === 'projects' && createPortal(
                <BulkProjectModal files={selectedFiles} onApplied={applied} onClose={close} />, document.body,
            )}
            {openModal === 'source' && createPortal(
                <BulkDerivationModal files={selectedFiles} onApplied={applied} onClose={close} />, document.body,
            )}
        </>
    );
}
